import React, { useState } from 'react';
import { useProducts } from '../context/ProductsContext';
import { iconMap } from '../pages/ShopPage';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiCheck, FiSearch,
  FiAlertTriangle, FiImage, FiRefreshCw,
} from 'react-icons/fi';

const CATEGORIES = ['Whole Spices', 'Powders', 'Blends & Masala', 'Sauces', 'Honey & Health'];
const ICON_TYPES = ['cool', 'chili', 'herb', 'wheat', 'florist'];
const BADGES = ['', 'Best Seller', 'New', 'Premium', 'Popular', 'Superfood', 'Natural', 'Value Pack', 'Organic', 'Sale'];

const emptyForm = {
  name: '', category: 'Powders', price: '', originalPrice: '',
  description: '', origin: 'Tanzania', weight: '50g',
  badge: '', iconType: 'cool', imageSrc: '', rating: '4.8', reviews: '100',
};

const ProductForm = ({ initial, onSave, onCancel, title }) => {
  const [form, setForm] = useState(initial || emptyForm);
  const [imgPreview, setImgPreview] = useState(initial?.imageSrc || '');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleImageFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { set('imageSrc', ev.target.result); setImgPreview(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.price) return;
    onSave({
      ...form,
      price: Number(form.price),
      originalPrice: form.originalPrice ? Number(form.originalPrice) : null,
      rating: Number(form.rating),
      reviews: Number(form.reviews),
      imageSrc: form.imageSrc || null,
      badge: form.badge || null,
    });
  };

  return (
    <div className="ap-form-overlay">
      <div className="ap-form-sheet">
        <div className="ap-form-header">
          <h2>{title}</h2>
          <button className="ap-icon-btn" onClick={onCancel}><FiX /></button>
        </div>
        <form onSubmit={handleSubmit} className="ap-form">
          <label className="ap-label">Product Name *
            <input className="ap-input" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Ginger Powder" />
          </label>

          <div className="ap-row">
            <label className="ap-label">Category *
              <select className="ap-input" value={form.category} onChange={e => set('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="ap-label">Badge
              <select className="ap-input" value={form.badge} onChange={e => set('badge', e.target.value)}>
                {BADGES.map(b => <option key={b} value={b}>{b || '— None —'}</option>)}
              </select>
            </label>
          </div>

          <div className="ap-row">
            <label className="ap-label">Price (TZS) *
              <input className="ap-input" type="number" value={form.price} onChange={e => set('price', e.target.value)} required placeholder="4500" />
            </label>
            <label className="ap-label">Original Price (TZS)
              <input className="ap-input" type="number" value={form.originalPrice} onChange={e => set('originalPrice', e.target.value)} placeholder="Leave blank if no discount" />
            </label>
          </div>

          <div className="ap-row">
            <label className="ap-label">Weight / Volume
              <input className="ap-input" value={form.weight} onChange={e => set('weight', e.target.value)} placeholder="50g" />
            </label>
            <label className="ap-label">Origin
              <input className="ap-input" value={form.origin} onChange={e => set('origin', e.target.value)} placeholder="Tanzania" />
            </label>
          </div>

          <label className="ap-label">Description *
            <textarea className="ap-input ap-textarea" value={form.description} onChange={e => set('description', e.target.value)} required rows={3} placeholder="Short product description..." />
          </label>

          <label className="ap-label">Fallback Icon
            <select className="ap-input" value={form.iconType} onChange={e => set('iconType', e.target.value)}>
              {ICON_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </label>

          <div className="ap-image-section">
            <p className="ap-label-text">Product Image</p>
            <div className="ap-image-row">
              {imgPreview && <img src={imgPreview} alt="preview" className="ap-img-preview" />}
              <div className="ap-image-inputs">
                <label className="ap-upload-btn">
                  <FiImage /> Upload Image
                  <input type="file" accept="image/*" onChange={handleImageFile} hidden />
                </label>
                <p className="ap-or">— or enter path —</p>
                <input className="ap-input" value={form.imageSrc} onChange={e => { set('imageSrc', e.target.value); setImgPreview(e.target.value); }}
                  placeholder="/assets/honey.png" />
              </div>
            </div>
          </div>

          <div className="ap-row">
            <label className="ap-label">Rating
              <input className="ap-input" type="number" step="0.1" min="1" max="5" value={form.rating} onChange={e => set('rating', e.target.value)} />
            </label>
            <label className="ap-label">Reviews Count
              <input className="ap-input" type="number" value={form.reviews} onChange={e => set('reviews', e.target.value)} />
            </label>
          </div>

          <div className="ap-form-actions">
            <button type="button" className="ap-btn-cancel" onClick={onCancel}>Cancel</button>
            <button type="submit" className="ap-btn-save"><FiCheck /> Save Product</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteConfirm = ({ name, onConfirm, onCancel }) => (
  <div className="ap-form-overlay">
    <div className="ap-confirm-sheet">
      <FiAlertTriangle className="ap-confirm-icon" />
      <h3>Delete Product?</h3>
      <p>Are you sure you want to delete <strong>{name}</strong>? This cannot be undone.</p>
      <div className="ap-form-actions">
        <button className="ap-btn-cancel" onClick={onCancel}>Cancel</button>
        <button className="ap-btn-delete" onClick={onConfirm}><FiTrash2 /> Delete</button>
      </div>
    </div>
  </div>
);

const AdminProducts = () => {
  const { products, addProduct, updateProduct, deleteProduct, resetToDefaults } = useProducts();
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showReset, setShowReset] = useState(false);

  const visible = products.filter(p =>
    (filterCat === 'All' || p.category === filterCat) &&
    (!search || p.name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <h1 className="ap-page-title">Products <span className="ap-count">{products.length}</span></h1>
        <button className="ap-btn-primary" onClick={() => setShowForm(true)}><FiPlus /> Add</button>
      </div>

      <div className="ap-search-row">
        <div className="ap-search-wrap">
          <FiSearch className="ap-search-icon" />
          <input className="ap-search" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="ap-filter-select" value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="All">All Categories</option>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
      </div>

      <div className="ap-product-list">
        {visible.map(p => (
          <div key={p.id} className="ap-product-row">
            <div className="ap-product-thumb">
              {p.imageSrc ? (
                <img src={p.imageSrc} alt={p.name} onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <span className="ap-product-icon">{iconMap[p.iconType] || iconMap.cool}</span>
              )}
            </div>
            <div className="ap-product-info">
              <p className="ap-product-name">{p.name}</p>
              <p className="ap-product-meta">{p.category} · {p.weight}</p>
              <p className="ap-product-price">TZS {Number(p.price).toLocaleString()}</p>
            </div>
            <div className="ap-product-actions">
              <button className="ap-icon-btn edit" onClick={() => setEditProduct(p)}><FiEdit2 /></button>
              <button className="ap-icon-btn del" onClick={() => setDeleteTarget(p)}><FiTrash2 /></button>
            </div>
          </div>
        ))}
        {visible.length === 0 && <p className="ap-empty">No products found.</p>}
      </div>

      <button className="ap-reset-btn" onClick={() => setShowReset(true)}>
        <FiRefreshCw /> Reset to Defaults
      </button>

      {showForm && (
        <ProductForm title="Add New Product"
          onSave={(data) => { addProduct(data); setShowForm(false); }}
          onCancel={() => setShowForm(false)} />
      )}
      {editProduct && (
        <ProductForm title="Edit Product" initial={editProduct}
          onSave={(data) => { updateProduct(editProduct.id, data); setEditProduct(null); }}
          onCancel={() => setEditProduct(null)} />
      )}
      {deleteTarget && (
        <DeleteConfirm name={deleteTarget.name}
          onConfirm={() => { deleteProduct(deleteTarget.id); setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)} />
      )}
      {showReset && (
        <div className="ap-form-overlay">
          <div className="ap-confirm-sheet">
            <FiAlertTriangle className="ap-confirm-icon" />
            <h3>Reset All Products?</h3>
            <p>This will discard all your changes and restore the original 38 products.</p>
            <div className="ap-form-actions">
              <button className="ap-btn-cancel" onClick={() => setShowReset(false)}>Cancel</button>
              <button className="ap-btn-delete" onClick={() => { resetToDefaults(); setShowReset(false); }}>Reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
